import { useAuthorizeUser } from "@coworker/apprestructured/src/shared/hooks/useAuthorizeUser";
import { SimpleDefaultLoadingScreen } from "@coworker/apprestructured/src/shared/simple/SimpleLoading/SimpleDefaultLoadingScreen";
import { Error } from "@coworker/apprestructured/src/login/components/Error/Error";
import { ErrorType } from "@coworker/apprestructured/src/shared/constants/errorTypes";
import { ReactElement } from "react";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";
import { NewUserPage } from "@coworker/apprestructured/src/login/components/NewUserPage/NewUserPage";
import { UnauthorizedPage } from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPage";
import { useUserHasFixaBasicPermissions } from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import useFlag, {
  FLAGS,
  useUserPermissionsQuery,
} from "@coworker/app/src/hooks/useFlag";

export const AuthorizedUserRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const userState = useAuthorizeUser();
  const { pathname } = useFixaLocation();

  // TO-DO: COAPP-12897 - remove the feature flag once deployed to all markets.
  // Wait for flags to load before evaluating — prevents a flash where the user
  // briefly accesses the app before the flag resolves to true.
  const { isLoading: isFlagLoading } = useUserPermissionsQuery();
  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);

  // Always check for fixa.basic permission from the Atkomst JWT.
  // Needed for new user self-provisioning: a brand-new Atkomst user has no DB entry yet,
  // so enableAtkomst (which comes from DB-scoped feature flags) is always false for them.
  // Uses MSAL acquireTokenSilent under the hood (may involve a network call on first run)
  // then decodes the JWT locally. staleTime is long to avoid repeated token acquisition.
  const {
    data: hasFixaBasicPermissions,
    isLoading: isFixaBasicPermissionsLoading,
  } = useUserHasFixaBasicPermissions(true);

  if (userState.isLoading || isFlagLoading || isFixaBasicPermissionsLoading) {
    return <SimpleDefaultLoadingScreen />;
  }

  if (enableAtkomst && !hasFixaBasicPermissions && !userState.isError) {
    return <UnauthorizedPage />;
  }

  // Redirect to NewUserPage for users that are not fully provisioned yet (Åtkomst markets only).
  // Guarded by hasFixaBasicPermissions (JWT-derived) rather than enableAtkomst (DB-derived flag)
  // so this works for brand-new Atkomst users who don't have a DB entry yet.
  // Allow /newuserteamselection to pass through so step 2 of the new user flow renders.
  if (hasFixaBasicPermissions && userState.errorType === ErrorType.NOTFOUND) {
    return pathname === "/newuserteamselection" ? children : <NewUserPage />;
  }

  // User has no Atkomst permission and no DB entry — show Unauthorized page so they
  // can request access via My Identity. Atkomst covers all IKEA markets so this is safe globally.
  if (!hasFixaBasicPermissions && userState.errorType === ErrorType.NOTFOUND) {
    return <UnauthorizedPage />;
  }

  if (userState.isError || userState.errorType === ErrorType.FORBIDDEN) {
    return (
      <Error isAccessIssue={userState.errorType === ErrorType.FORBIDDEN} />
    );
  }

  return userState?.isActive ? children : <Error isAccessIssue={false} />;
};
